import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Pagination, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';

export default function UtilityExpenseIndex() {
    const { t } = useContext(TranslatorContext);

    const [utilityExpenses, setUtilityExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertModal, setAlertModal] = useState(false);
    const [formModal, setFormModal] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Form data
    const [formData, setFormData] = useState({
        date: '',
        expense_category_id: '',
        amount: '',
        comment: '',
        payments: [{ amount: '', payment_method_id: '' }]
    });

    // Filter states
    const [filters, setFilters] = useState({
        from_date: '',
        to_date: '',
        date: ''
    });

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);

    // Debounce timeout for filters
    const [filterTimeout, setFilterTimeout] = useState(null);

    useEffect(() => {
        fetchUtilityExpenses();
    }, [currentPage, perPage]);

    // Apply filters when they change
    useEffect(() => {
        if (filterTimeout) clearTimeout(filterTimeout);
        setFilterTimeout(setTimeout(() => {
            setCurrentPage(1);
            fetchUtilityExpenses();
        }, 500));
    }, [filters]);

    const fetchUtilityExpenses = () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        let url = `${process.env.REACT_APP_API_URL}/utility-expenses?page=${currentPage}&per_page=${perPage}`;

        // Add filters to URL
        if (filters.from_date) {
            url += `&from_date=${filters.from_date}`;
        }
        if (filters.to_date) {
            url += `&to_date=${filters.to_date}`;
        }
        if (filters.date) {
            url += `&date=${filters.date}`;
        }

        axios.get(url, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                if (res.data.utilityExpenses) {
                    setUtilityExpenses(res.data.utilityExpenses.data || []);
                    setTotal(res.data.utilityExpenses.total || 0);
                    setCurrentPage(res.data.utilityExpenses.current_page || 1);
                    setLastPage(res.data.utilityExpenses.last_page || 1);
                }
                if (res.data.expenseCategories) {
                    setExpenseCategories(res.data.expenseCategories);
                }
                if (res.data.paymentMethods) {
                    setPaymentMethods(res.data.paymentMethods);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const resetFilters = () => {
        setFilters({
            from_date: '',
            to_date: '',
            date: ''
        });
        setCurrentPage(1);
        setTimeout(() => fetchUtilityExpenses(), 100);
    };

    const handleOpenModal = (expense = null) => {
        if (expense) {
            setIsEditing(true);
            setCurrentId(expense.id);
            setFormData({
                date: expense.date || '',
                expense_category_id: expense.expense_category_id || '',
                amount: expense.amount || '',
                comment: expense.comment || '',
                payments: expense.payments?.map(p => ({
                    amount: p.amount,
                    payment_method_id: p.payment_method_id
                })) || [{ amount: '', payment_method_id: '' }]
            });
        } else {
            setIsEditing(false);
            setCurrentId(null);
            setFormData({
                date: new Date().toISOString().split('T')[0],
                expense_category_id: '',
                amount: '',
                comment: '',
                payments: [{ amount: '', payment_method_id: '' }]
            });
        }
        setFormModal(true);
    };

    const handleCloseModal = () => {
        setFormModal(false);
        setFormData({
            date: '',
            expense_category_id: '',
            amount: '',
            comment: '',
            payments: [{ amount: '', payment_method_id: '' }]
        });
        setIsEditing(false);
        setCurrentId(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePaymentChange = (index, field, value) => {
        const updatedPayments = [...formData.payments];
        updatedPayments[index][field] = value;
        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    const addPaymentRow = () => {
        setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, { amount: '', payment_method_id: '' }]
        }));
    };

    const removePaymentRow = (index) => {
        if (formData.payments.length === 1) return;
        const updatedPayments = formData.payments.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem("authToken");

        // Validate total payments match amount
        const totalPayments = formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        if (Math.abs(totalPayments - parseFloat(formData.amount)) > 0.01) {
            Swal.fire({
                icon: 'error',
                title: t('validation error'),
                text: t('Total payments amount must equal the expense amount'),
                confirmButtonText: t('ok')
            });
            return;
        }

        try {
            let response;
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            };

            if (isEditing) {
                response = await axios.put(
                    `${process.env.REACT_APP_API_URL}/utility-expenses/${currentId}`,
                    formData,
                    config
                );
            } else {
                response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/utility-expenses`,
                    formData,
                    config
                );
            }

            if (response.data) {
                handleCloseModal();
                fetchUtilityExpenses();

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: isEditing ? t('Utility expense updated successfully') : t('Utility expense created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            console.error('Error saving utility expense:', error);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.values(errors).flat().join('\n');
                Swal.fire({
                    icon: 'error',
                    title: t('validation error'),
                    text: errorMessages,
                    confirmButtonText: t('ok')
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response?.data?.message || t('Failed to save utility expense'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        }
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/utility-expenses/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAlertModal(false);
            fetchUtilityExpenses();

            Swal.fire({
                icon: 'success',
                title: t('success'),
                text: t('Utility expense deleted successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error deleting utility expense:', error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete utility expense'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        }
    };

    const confirmDelete = (id) => {
        setCurrentId(id);
        setAlertModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0';
        return parseFloat(amount).toFixed(2);
    };

    // Get category name by id
    const getCategoryName = (categoryId) => {
        const category = expenseCategories.find(cat => cat.id === categoryId);
        return category ? category.name : '-';
    };

    // Pagination helpers
    const getPaginationItems = () => {
        let items = [];
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(lastPage, currentPage + 2);

        if (endPage - startPage < 4) {
            if (startPage > 1) {
                startPage = Math.max(1, endPage - 4);
            }
            if (endPage < lastPage) {
                endPage = Math.min(lastPage, startPage + 4);
            }
        }

        if (startPage > 1) {
            items.push(<Pagination.First key="first" onClick={() => handlePageChange(1)} />);
            items.push(<Pagination.Ellipsis key="ellipsis-start" />);
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < lastPage) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            items.push(<Pagination.Last key="last" onClick={() => handlePageChange(lastPage)} />);
        }

        return items;
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage && newPage !== currentPage) {
            setCurrentPage(newPage);
        }
    };

    if (loading && utilityExpenses.length === 0) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('utility expenses')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('expenses')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('utility expenses')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {/* Header with Add Button */}
                        <div className="mc-card-header d-flex justify-content-between align-items-center p-3">
                            <h5 className="mc-card-title mb-0">{t('utility expenses list')}</h5>
                            {hasPermission("Utility Expenses create") && (
                                <ButtonComponent
                                    type="button"
                                    className="btn btn-primary d-flex align-items-center"
                                    onClick={() => handleOpenModal()}
                                >
                                    <i className="material-icons">add</i>
                                    {t('Add Utility Expense')}
                                </ButtonComponent>
                            )}
                        </div>

                        {/* Filters Section */}
                        <div className="p-3 border-bottom">
                            <Row className="align-items-end g-3">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">{t('from date')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="from_date"
                                            value={filters.from_date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">{t('to date')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="to_date"
                                            value={filters.to_date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">{t('specific date')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="date"
                                            value={filters.date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex gap-2">
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={resetFilters}
                                        >
                                            <i className="material-icons me-1" style={{ fontSize: '16px' }}>refresh</i>
                                            {t('reset')}
                                        </ButtonComponent>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>{t('date')}</th>
                                                    <th>{t('category')}</th>
                                                    <th>{t('amount')}</th>
                                                    <th>{t('payment method')}</th>
                                                    <th>{t('comment')}</th>
                                                    <th>{t('actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {utilityExpenses.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-5">
                                                            <i className="material-icons" style={{ fontSize: '48px' }}>inbox</i>
                                                            <p className="mt-2">{t('no utility expenses found')}</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    utilityExpenses.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <div className="mc-table-check">
                                                                    <p>{((currentPage - 1) * perPage) + index + 1}</p>
                                                                </div>
                                                            </td>
                                                            <td>{formatDate(item.date)}</td>
                                                            <td>{item.category?.name || getCategoryName(item.expense_category_id)}</td>
                                                            <td>{formatCurrency(item.amount)}</td>
                                                            <td>
                                                                {item.payments?.map((p, i) => (
                                                                    <div key={i}>
                                                                        {p.method?.name}: {formatCurrency(p.amount)}
                                                                    </div>
                                                                ))}
                                                            </td>
                                                            <td>
                                                                <div style={{ maxWidth: '200px', whiteSpace: 'normal' }}>
                                                                    {item.comment || '-'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="mc-table-action">
                                                                    {hasPermission("Utility Expenses edit") && (
                                                                        <ButtonComponent
                                                                            type="button"
                                                                            className="material-icons edit"
                                                                            onClick={() => handleOpenModal(item)}
                                                                            title={t('edit')}
                                                                        >
                                                                            edit
                                                                        </ButtonComponent>
                                                                    )}
                                                                    {/* <ButtonComponent
                                                                        type="button"
                                                                        className="material-icons delete"
                                                                        onClick={() => confirmDelete(item.id)}
                                                                        title={t('delete')}
                                                                    >
                                                                        delete
                                                                    </ButtonComponent> */}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Bootstrap Pagination */}
                                        {total > 0 && (
                                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                                <div className="text-muted small">
                                                    {t('showing')} {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, total)} {t('of')} {total} {t('entries')}
                                                </div>
                                                <div className="d-flex gap-3 align-items-center">

                                                    <Pagination size="sm" className="mb-0">
                                                        <Pagination.Prev
                                                            onClick={() => handlePageChange(currentPage - 1)}
                                                            disabled={currentPage === 1}
                                                        />
                                                        {getPaginationItems()}
                                                        <Pagination.Next
                                                            onClick={() => handlePageChange(currentPage + 1)}
                                                            disabled={currentPage === lastPage}
                                                        />
                                                    </Pagination>
                                                </div>
                                            </div>
                                        )}

                                        {/* Add/Edit Modal with Fixed Height */}
                                        <Modal show={formModal} onHide={handleCloseModal} size="lg" centered>
                                            <Modal.Header closeButton className="p-3">
                                                <Modal.Title>
                                                    {isEditing ? t('Edit Utility Expense') : t('Add New Utility Expense')}
                                                </Modal.Title>
                                            </Modal.Header>
                                            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                                <Form onSubmit={handleSubmit}>
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('date')} *</Form.Label>
                                                                <Form.Control
                                                                    type="date"
                                                                    name="date"
                                                                    value={formData.date}
                                                                    onChange={handleInputChange}
                                                                    required
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('expense category')} *</Form.Label>
                                                                <Form.Select
                                                                    name="expense_category_id"
                                                                    value={formData.expense_category_id}
                                                                    onChange={handleInputChange}
                                                                    required
                                                                >
                                                                    <option value="">{t('Select Category')}</option>
                                                                    {expenseCategories.map(cat => (
                                                                        <option key={cat.id} value={cat.id}>
                                                                            {cat.name}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('amount')} *</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    name="amount"
                                                                    value={formData.amount}
                                                                    onChange={handleInputChange}
                                                                    placeholder={t('Enter expense amount')}
                                                                    required
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={12}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('comment')}</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={2}
                                                                    name="comment"
                                                                    value={formData.comment}
                                                                    onChange={handleInputChange}
                                                                    placeholder={t('Enter comment (optional)')}
                                                                />
                                                            </Form.Group>
                                                        </Col>

                                                        {/* Payments Section */}
                                                        <Col md={12}>
                                                            <div className="border-top pt-3 mt-2">
                                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                                    <Form.Label className="fw-bold mb-0">{t('payment details')}</Form.Label>
                                                                    <ButtonComponent
                                                                        type="button"
                                                                        className="btn btn-sm btn-success"
                                                                        onClick={addPaymentRow}
                                                                    >
                                                                        <i className="material-icons" style={{ fontSize: '16px' }}>add</i>
                                                                        {t('Add Payment')}
                                                                    </ButtonComponent>
                                                                </div>

                                                                {formData.payments.map((payment, idx) => (
                                                                    <Row key={idx} className="mb-2 align-items-end">
                                                                        <Col md={5}>
                                                                            <Form.Group>
                                                                                <Form.Label>{t('amount')}</Form.Label>
                                                                                <Form.Control
                                                                                    type="number"
                                                                                    step="0.01"
                                                                                    value={payment.amount}
                                                                                    onChange={(e) => handlePaymentChange(idx, 'amount', e.target.value)}
                                                                                    placeholder={t('Payment amount')}
                                                                                    required
                                                                                />
                                                                            </Form.Group>
                                                                        </Col>
                                                                        <Col md={5}>
                                                                            <Form.Group>
                                                                                <Form.Label>{t('payment method')}</Form.Label>
                                                                                <Form.Select
                                                                                    value={payment.payment_method_id}
                                                                                    onChange={(e) => handlePaymentChange(idx, 'payment_method_id', e.target.value)}
                                                                                    required
                                                                                >
                                                                                    <option value="">{t('Select Method')}</option>
                                                                                    {paymentMethods.map(method => (
                                                                                        <option key={method.id} value={method.id}>
                                                                                            {method.name} {method.account_number ? `(${method.account_number})` : ''}
                                                                                        </option>
                                                                                    ))}
                                                                                </Form.Select>
                                                                            </Form.Group>
                                                                        </Col>
                                                                        <Col md={2}>
                                                                            <ButtonComponent
                                                                                type="button"
                                                                                className="btn btn-sm btn-danger w-100"
                                                                                onClick={() => removePaymentRow(idx)}
                                                                                disabled={formData.payments.length === 1}
                                                                            >
                                                                                <i className="material-icons" style={{ fontSize: '16px' }}>delete</i>
                                                                            </ButtonComponent>
                                                                        </Col>
                                                                    </Row>
                                                                ))}

                                                                <div className="mt-2 text-muted small">
                                                                    <strong>{t('Total payments')}:</strong> {formatCurrency(formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}
                                                                    {formData.amount && (
                                                                        <span className={parseFloat(formData.amount) !== formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) ? "text-danger" : "text-success"}>
                                                                            {parseFloat(formData.amount) !== formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
                                                                                ? ` (${t('Difference')}: ${formatCurrency(Math.abs(parseFloat(formData.amount) - formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)))})`
                                                                                : ` (${t('Matched')})`}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <Modal.Footer className="p-3 px-0">
                                                        <ButtonComponent
                                                            type="button"
                                                            className="btn btn-secondary"
                                                            onClick={handleCloseModal}
                                                        >
                                                            {t('cancel')}
                                                        </ButtonComponent>
                                                        <ButtonComponent
                                                            type="submit"
                                                            className="btn btn-primary"
                                                        >
                                                            {isEditing ? t('update') : t('save')}
                                                        </ButtonComponent>
                                                    </Modal.Footer>
                                                </Form>
                                            </Modal.Body>
                                        </Modal>

                                        {/* Delete Confirmation Modal */}
                                        <Modal show={alertModal} onHide={() => setAlertModal(false)} centered>
                                            <div className="mc-alert-modal p-4 text-center">
                                                <i className="material-icons" style={{ fontSize: '50px', color: 'red' }}>warning</i>
                                                <h3 className="mt-3">{t("Are you sure?")}</h3>
                                                <p>{t("Want to delete this utility expense?")}</p>
                                                <div className="d-flex justify-content-center gap-2 mt-3">
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setAlertModal(false)}
                                                    >
                                                        {t('cancel')}
                                                    </ButtonComponent>
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-danger"
                                                        onClick={() => handleDelete(currentId)}
                                                    >
                                                        {t('delete')}
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        </Modal>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}